'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import PhaseCard from '@/app/our-approach/components/PhaseCard';
import IntegrationPoint from '@/app/our-approach/components/IntegrationPoint';
import TimelineVisualization from '@/app/our-approach/components/TimelineVisualization';
import SuccessMetrics from '@/app/our-approach/components/SuccessMetrics';

interface Activity {
  title: string;
  description: string;
}

interface Deliverable {
  name: string;
  icon: string;
}

interface Phase {
  phaseNumber: number;
  title: string;
  subtitle: string;
  duration: string;
  activities: Activity[];
  deliverables: Deliverable[];
  keyOutcomes: string[];
}

interface IntegrationPointData {
  title: string;
  description: string;
  icon: string;
  phases: string[];
}

interface Milestone {
  phase: string;
  month: string;
  title: string;
  description: string;
}

interface Metric {
  phase: string;
  category: string;
  metrics: string[];
  icon: string;
}

const OurApproachInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const phases: Phase[] = [
    {
      phaseNumber: 1,
      title: "Diagnostic & Assessment",
      subtitle: "Understanding institutional context and capacity gaps",
      duration: "4-6 weeks",
      activities: [
        {
          title: "Institutional Analysis",
          description: "Comprehensive review of governance structures, decision-making processes, and organizational capabilities"
        },
        {
          title: "Stakeholder Mapping",
          description: "Identification of key actors, power dynamics, and institutional relationships affecting transformation"
        },
        {
          title: "Capacity Assessment",
          description: "Evaluation of leadership capabilities, technical skills, and organizational readiness for change"
        },
        {
          title: "Strategic Context Review",
          description: "Analysis of policy environment, regulatory frameworks, and external factors influencing strategy"
        }
      ],
      deliverables: [
        { name: "Diagnostic Report", icon: "DocumentTextIcon" },
        { name: "Stakeholder Map", icon: "UserGroupIcon" },
        { name: "Capacity Gap Analysis", icon: "ChartBarIcon" },
        { name: "Baseline Assessment", icon: "ClipboardDocumentCheckIcon" }
      ],
      keyOutcomes: [
        "Clear understanding of institutional strengths and constraints",
        "Identified priority areas for strategic intervention",
        "Mapped stakeholder landscape and engagement requirements",
        "Established baseline metrics for measuring progress"
      ]
    },
    {
      phaseNumber: 2,
      title: "Strategy Design & Framework Development",
      subtitle: "Creating executable strategies with governance structures",
      duration: "6-8 weeks",
      activities: [
        {
          title: "Strategic Framework Design",
          description: "Development of institutional strategy aligned with mandate, resources, and political realities"
        },
        {
          title: "Governance Architecture",
          description: "Design of decision-making structures, accountability mechanisms, and oversight frameworks"
        },
        {
          title: "Execution Planning",
          description: "Creation of phased implementation roadmap with clear milestones and resource requirements"
        },
        {
          title: "Risk Mitigation Framework",
          description: "Identification of implementation risks and development of mitigation strategies"
        }
      ],
      deliverables: [
        { name: "Strategic Framework", icon: "MapIcon" },
        { name: "Governance Model", icon: "BuildingOfficeIcon" },
        { name: "Implementation Roadmap", icon: "CalendarDaysIcon" },
        { name: "Risk Register", icon: "ShieldCheckIcon" }
      ],
      keyOutcomes: [
        "Executable strategy grounded in institutional reality",
        "Clear governance structures for decision-making and accountability",
        "Phased implementation plan with realistic timelines",
        "Identified risks with proactive mitigation approaches"
      ]
    },
    {
      phaseNumber: 3,
      title: "Capacity Building & Implementation Support",
      subtitle: "Developing leadership and organizational capabilities",
      duration: "12-16 weeks",
      activities: [
        {
          title: "Leadership Development",
          description: "Executive coaching and capability building for senior decision-makers and change champions"
        },
        {
          title: "System Design & Setup",
          description: "Implementation of performance management, monitoring systems, and operational frameworks"
        },
        {
          title: "Team Capability Building",
          description: "Training and development programs for technical teams and operational staff"
        },
        {
          title: "Change Management Support",
          description: "Guidance on stakeholder engagement, communication, and organizational change processes"
        }
      ],
      deliverables: [
        { name: "Leadership Program", icon: "AcademicCapIcon" },
        { name: "Performance Systems", icon: "ChartPieIcon" },
        { name: "Training Materials", icon: "BookOpenIcon" },
        { name: "Change Toolkit", icon: "WrenchScrewdriverIcon" }
      ],
      keyOutcomes: [
        "Enhanced leadership capabilities for strategic execution",
        "Operational systems supporting performance and accountability",
        "Skilled teams capable of implementing strategic initiatives",
        "Effective change management processes in place"
      ]
    },
    {
      phaseNumber: 4,
      title: "Embedding & Sustainability",
      subtitle: "Ensuring long-term institutional ownership and capability",
      duration: "8-12 weeks",
      activities: [
        {
          title: "Performance Monitoring",
          description: "Tracking implementation progress, measuring outcomes, and adjusting approaches as needed"
        },
        {
          title: "Knowledge Transfer",
          description: "Systematic transfer of methodologies, tools, and capabilities to institutional teams"
        },
        {
          title: "Sustainability Planning",
          description: "Development of mechanisms for ongoing capability development and continuous improvement"
        },
        {
          title: "Transition Support",
          description: "Gradual reduction of external support while ensuring institutional ownership and confidence"
        }
      ],
      deliverables: [
        { name: "Performance Dashboard", icon: "PresentationChartLineIcon" },
        { name: "Knowledge Repository", icon: "FolderOpenIcon" },
        { name: "Sustainability Plan", icon: "ArrowPathIcon" },
        { name: "Transition Framework", icon: "ArrowRightCircleIcon" }
      ],
      keyOutcomes: [
        "Demonstrated progress on strategic objectives and key results",
        "Institutional teams capable of independent execution",
        "Embedded systems for ongoing performance and improvement",
        "Successful transition to full institutional ownership"
      ]
    }
  ];

  const integrationPoints: IntegrationPointData[] = [
    {
      title: "Strategy-Execution Integration",
      description: "Strategic frameworks are designed with execution realities in mind, ensuring plans are grounded in institutional capacity and political context.",
      icon: "LinkIcon",
      phases: ["Phase 1", "Phase 2", "Phase 3"]
    },
    {
      title: "Leadership-System Alignment",
      description: "Leadership development is synchronized with system design, ensuring executives have both capability and tools to drive transformation.",
      icon: "UserGroupIcon",
      phases: ["Phase 2", "Phase 3", "Phase 4"]
    },
    {
      title: "Continuous Stakeholder Engagement",
      description: "Stakeholder mapping informs governance design, capacity building, and change management throughout the engagement lifecycle.",
      icon: "ChatBubbleLeftRightIcon",
      phases: ["Phase 1", "Phase 2", "Phase 3", "Phase 4"]
    },
    {
      title: "Performance-Learning Feedback",
      description: "Monitoring systems provide real-time insights that inform ongoing capacity building and strategic adjustments.",
      icon: "ArrowPathRoundedSquareIcon",
      phases: ["Phase 3", "Phase 4"]
    }
  ];

  const milestones: Milestone[] = [
    {
      phase: "Phase 1",
      month: "Month 1-2",
      title: "Diagnostic Complete",
      description: "Institutional assessment and baseline established"
    },
    {
      phase: "Phase 2",
      month: "Month 3-4",
      title: "Strategy Approved",
      description: "Strategic framework and governance model finalized"
    },
    {
      phase: "Phase 3",
      month: "Month 5-8",
      title: "Capabilities Built",
      description: "Leadership and systems development in progress"
    },
    {
      phase: "Phase 4",
      month: "Month 9-12",
      title: "Ownership Transferred",
      description: "Sustainable institutional capability achieved"
    }
  ];

  const successMetrics: Metric[] = [
    {
      phase: "Phase 1",
      category: "Diagnostic Quality",
      icon: "MagnifyingGlassIcon",
      metrics: [
        "Comprehensiveness of institutional analysis",
        "Accuracy of stakeholder mapping",
        "Validity of capacity gap identification",
        "Stakeholder agreement on baseline assessment"
      ]
    },
    {
      phase: "Phase 2",
      category: "Strategy Robustness",
      icon: "DocumentCheckIcon",
      metrics: [
        "Alignment with institutional mandate and resources",
        "Clarity of governance and accountability structures",
        "Feasibility of implementation roadmap",
        "Executive commitment to strategic framework"
      ]
    },
    {
      phase: "Phase 3",
      category: "Capability Development",
      icon: "AcademicCapIcon",
      metrics: [
        "Leadership competency improvements",
        "System functionality and adoption rates",
        "Team skill development progress",
        "Change management effectiveness"
      ]
    },
    {
      phase: "Phase 4",
      category: "Sustainability Achievement",
      icon: "CheckBadgeIcon",
      metrics: [
        "Progress on strategic objectives",
        "Institutional ownership and confidence",
        "System utilization and performance",
        "Capability for independent execution"
      ]
    }
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-surface rounded w-3/4"></div>
            <div className="h-6 bg-surface rounded w-1/2"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-64 bg-surface rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Content Container */}
      <div className="space-y-12 sm:space-y-16 py-10 sm:py-16">
        {/* Phases Section */}
        <section className="container mx-auto px-4 sm:px-6">
          <div className="space-y-6 sm:space-y-8 lg:space-y-0">
            {phases.map((phase, index) => (
              <PhaseCard
                key={phase.phaseNumber}
                {...phase}
                isLast={index === phases.length - 1}
              />
            ))}
          </div>
        </section>

        {/* Integration Points Section */}
        <section className="bg-surface py-12 sm:py-16 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary mb-3 sm:mb-4">
                Integration Throughout the Process
              </h2>
              <p className="text-sm sm:text-base text-text-secondary max-w-3xl mx-auto px-2">
                Our methodology ensures strategy and capacity building work together seamlessly, creating sustainable institutional transformation.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {integrationPoints.map((point, index) => (
                <IntegrationPoint key={index} {...point} />
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="container mx-auto px-4 sm:px-6">
          <TimelineVisualization milestones={milestones} />
        </section>

        {/* Success Metrics Section */}
        <section className="container mx-auto px-4 sm:px-6">
          <SuccessMetrics metrics={successMetrics} />
        </section>
      </div>
    </>
  );
};

export default OurApproachInteractive;